import { useEffect, useState } from "react";
import { Shield, AlertTriangle } from "lucide-react";
import { cn } from "../../../lib/utils.js";
import { useTranslation } from "react-i18next";
import { FeedBotAnimatedMark } from "../../../components/brand/FeedBotAnimatedMark.js";
import { useAuth } from "../auth-context.js";
import { AuthAlert, AuthEntryShell, GoogleSignInButton } from "../entry/index.js";
import { prefersOauthRedirect } from "../prefer-oauth-redirect.js";
import { startGoogleAuthentication } from "../start-google-authentication.js";
import { useGooglePopup } from "../use-google-popup.js";
import { googleStartUrl } from "../../../api/auth-api.js";
import { markFreshAuthentication } from "../../organizations/workspace-entry.js";
import { Button } from "../../../components/ui/button/index.js";

const POPUP_CLOSED_DISMISS_MS = 5000;
const BRAND_MARK_REPLAY_MS = 7_000;

export function LoginPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { retry, setAuthenticating } = useAuth();
  const [redirectAuthError, setRedirectAuthError] = useState<string | null>(null);
  
  // משתנה State חדש לניהול אנימציית הסגירה הרכה של ההערה
  const [isNoticeExiting, setIsNoticeExiting] = useState(false);

  const { status, start, reset } = useGooglePopup(() => {
    setAuthenticating();
    retry();
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authError = params.get("authError");
    if (authError) {
      setRedirectAuthError(authError);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // לוגיקה מעודכנת לסגירה אוטומטית שמשלבת את האנימציה
  useEffect(() => {
    if (status.kind !== "closed") {
      setIsNoticeExiting(false);
      return;
    }
    
    let exitTimer: number;
    const mainTimer = window.setTimeout(() => {
      setIsNoticeExiting(true); // הפעלת אנימציית יציאה
      exitTimer = window.setTimeout(() => reset(), 300); // איפוס מלא אחרי 300ms
    }, POPUP_CLOSED_DISMISS_MS - 300);
    
    return () => {
      window.clearTimeout(mainTimer);
      window.clearTimeout(exitTimer);
    };
  }, [status.kind, reset]);

  // פונקציה לסגירה ידנית עם אנימציה
  const handleCloseNotice = () => {
    setIsNoticeExiting(true);
    setTimeout(() => reset(), 300);
  };

  const handleRedirectFallback = () => {
    markFreshAuthentication();
    window.location.href = googleStartUrl("redirect", "/");
  };

  const handleGoogleClick = () => {
    setRedirectAuthError(null);
    if (status.kind === "closed" || status.kind === "error" || status.kind === "blocked") {
      reset();
    }
    startGoogleAuthentication({
      preferRedirect: prefersOauthRedirect(),
      startPopup: start,
    });
  };

  const isOpening = status.kind === "opening";

  return (
    <AuthEntryShell>
      <div className="grid justify-items-center text-center">
        <FeedBotAnimatedMark
          label={t("auth.wordmarkLabel")}
          replayIntervalMs={BRAND_MARK_REPLAY_MS}
          className="mb-[18px] size-[54px] md:mb-[22px] md:size-[58px]"
        />
        <p className="m-0 inline-flex items-center gap-[5px] rounded-full bg-[#def5e7] px-[9px] py-[3px] text-[11px] leading-none font-[700] text-[#13764e]">
          <Shield size={12} strokeWidth={2.25} aria-hidden="true" />
          {t("auth.secureWorkspace")}
        </p>
        <h1 className="m-0 mt-[15px] text-[24px] leading-[1.2] font-[700] tracking-[-0.05em] text-[#12372e] wrap-anywhere md:hidden">
          {t("auth.title")}
        </h1>
        <h2 className="m-0 mt-[15px] hidden text-[27px] leading-[1.2] font-[700] tracking-[-0.05em] text-[#12372e] wrap-anywhere md:block">
          {t("auth.title")}
        </h2>
        <p className="m-0 mt-[9px] mb-[26px] text-sm leading-normal text-[#82958b] wrap-anywhere">
          {t("auth.subtitle")}
        </p>

        <div className="grid w-full gap-3 text-start">
          {status.kind === "blocked" ? (
            <AuthAlert
              actions={
                <>
                  <Button variant="link" onClick={handleRedirectFallback}>
                    {t("auth.popupBlocked.redirect")}
                  </Button>
                  <Button variant="link" onClick={reset}>
                    {t("auth.popupBlocked.retry")}
                  </Button>
                </>
              }
            >
              {t("auth.popupBlocked.message")}
            </AuthAlert>
          ) : null}

          {status.kind === "closed" ? (
            <div 
              className={cn(
                "flex w-full items-center justify-between gap-3 rounded-[12px] border border-[#f5df98] bg-[#fffcf0] px-4 py-3 text-[#8c6b14] shadow-sm transition-all duration-300 ease-out origin-top",
                isNoticeExiting ? "scale-95 opacity-0 -translate-y-2" : "animate-in fade-in zoom-in-95 slide-in-from-top-2"
              )}
              data-testid="auth-popup-closed-notice"
            >
              <div className="flex items-center gap-2.5">
                <AlertTriangle size={18} className="shrink-0" aria-hidden="true" />
                <span className="text-sm font-[600] leading-tight wrap-anywhere">
                  {t("auth.popupClosed.message")}
                </span>
              </div>
              <Button
                variant="ghost"
                onClick={handleCloseNotice}
                className="h-auto shrink-0 p-0 text-sm font-[800] text-[#8c6b14] hover:bg-transparent hover:text-[#5e4709] focus-visible:ring-offset-[#fffcf0] transition-colors"
              >
                {t("common.close")}
              </Button>
            </div>
          ) : null}

          {status.kind === "error" ? (
            <AuthAlert
              actions={
                <Button variant="link" onClick={reset}>
                  {t("auth.error.retry")}
                </Button>
              }
            >
              {t("auth.error.message", { code: status.code })}
            </AuthAlert>
          ) : null}

          {redirectAuthError ? (
            <AuthAlert
              actions={
                <Button
                  variant="link"
                  onClick={() => {
                    setRedirectAuthError(null);
                    reset();
                  }}
                >
                  {t("auth.error.retry")}
                </Button>
              }
            >
              {t("auth.error.message", { code: redirectAuthError })}
            </AuthAlert>
          ) : null}

          <GoogleSignInButton
            label={t("auth.continueWithGoogle")}
            loadingLabel={t("auth.opening")}
            loading={isOpening}
            disabled={isOpening}
            onClick={handleGoogleClick}
          />
        </div>

        <p className="m-0 mt-[22px] text-center text-[11px] leading-normal text-[#81988c] wrap-anywhere">
          {t("auth.hint")}
        </p>
      </div>
    </AuthEntryShell>
  );
}